import type { Section, SectionPlanItem } from "../contract"
import { walkBlocks } from "./format"

/** The six sections of every answer, in order (answer-anatomy.md). 06 comes from answer.done limits. */
export const SLOTS = [
  { tag: "LIMIT", num: "01", short: "Time limit", title: "Is the notice in time?" },
  { tag: "TURNS", num: "02", short: "Turns on", title: "What this notice turns on" },
  { tag: "COURTS", num: "03", short: "Decisions", title: "Decisions, for and against" },
  { tag: "STAKE", num: "04", short: "At stake", title: "What it could cost" },
  { tag: "ACTION", num: "05", short: "By when", title: "What to do, by when" },
  { tag: "LIMITS", num: "06", short: "Not seen", title: "What Nira cannot see" },
] as const

export type SlotTag = (typeof SLOTS)[number]["tag"]

export interface Slot {
  tag: SlotTag
  num: string
  short: string
  title: string
  /** DOM id; the rail, the glance and Prev/Next jump here. */
  id: string
  /** The planned item and its index in the plan, when the model planned this section. */
  item?: SectionPlanItem
  index?: number
  section?: Section
  /** 06 only: what Nira could not see. */
  limits?: string[]
  /** Reading time in minutes, from the words in it. */
  minutes: number
}

export const slotId = (turn: string, num: string) => `sec-${turn}-${num}`

const ANATOMY_TAGS = new Set<string>(SLOTS.slice(0, 5).map((s) => s.tag))

/** True when the plan uses the fixed anatomy (answers from before it keep their own headings). */
export const isAnatomy = (plan: SectionPlanItem[]) => plan.length > 0 && plan.every((p) => ANATOMY_TAGS.has(p.tag.toUpperCase()))

function words(section: Section | undefined, extra = ""): number {
  let n = extra.split(/\s+/).filter(Boolean).length
  if (section) {
    walkBlocks(section.blocks ?? [], (b) => {
      n += JSON.stringify(b).split(/\s+/).length * 0.6
    })
  }
  return n
}

/** The six slots for one turn. */
export function slotsFor(turn: string, plan: SectionPlanItem[], sections: Record<string, Section>, limits: string[]): Slot[] {
  return SLOTS.map((s) => {
    const index = plan.findIndex((p) => p.tag.toUpperCase() === s.tag)
    const item = index >= 0 ? plan[index] : undefined
    const section = item ? sections[item.id] : undefined
    const text = s.tag === "LIMITS" ? limits.join(" ") : item?.takeaway ?? ""
    return {
      ...s,
      id: slotId(turn, s.num),
      item,
      index: index >= 0 ? index : undefined,
      section,
      limits: s.tag === "LIMITS" ? limits : undefined,
      minutes: Math.max(0.25, words(section, text) / 220),
    }
  })
}

/** "about a minute", "about 3 minutes". */
export function readingTime(minutes: number): string {
  const m = Math.round(minutes)
  return m <= 1 ? "about a minute" : `about ${m} minutes`
}
